import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { fmtEUR, fmtDate, formatErr } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { ArrowLeft, Clock, FileText, Layers, Paperclip, Check, RotateCcw, Download, Euro, Palette, MoreHorizontal, Trash2, Pause, Play, Ban, CheckCircle2, Archive, ArchiveRestore, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProjektChat from "@/components/ProjektChat";

const TABS_META = [
  { id: "ueberblick", labelKey: "project.tab.ueberblick", icon: Layers },
  { id: "entwuerfe",  labelKey: "project.tab.entwuerfe", icon: Paperclip },
  { id: "chat",       labelKey: "detail.chat_tab", icon: MessageSquare },
  { id: "zeiten",     labelKey: "project.tab.zeiten", icon: Clock },
  { id: "abrechnung", labelKey: "project.tab.abrechnung", icon: Euro, hideFor: ["designer"] },
  { id: "briefing",   labelKey: "project.tab.briefing", icon: FileText },
  { id: "ci",         labelKey: "project.tab.ci", icon: Palette },
];

export default function ProjektDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const t = useT();
  const isDesigner = user?.rolle === "designer";
  const canEdit = user?.rolle === "admin" || user?.rolle === "pm";

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "ueberblick";
  const [tab, setTab] = useState(initialTab);
  const [projekt, setProjekt] = useState(null);
  const [briefings, setBriefings] = useState([]);
  const [zeiten, setZeiten] = useState([]);
  const [entwuerfe, setEntwuerfe] = useState([]);
  const [err, setErr] = useState("");

  const reload = async () => {
    const [p, b, z, e] = await Promise.all([
      api.get(`/projekte/${id}`),
      api.get(`/projekte/${id}/briefings`),
      api.get(`/projekte/${id}/zeiten`),
      api.get(`/projekte/${id}/entwuerfe`),
    ]);
    setProjekt(p.data);
    setBriefings(b.data);
    setZeiten(z.data);
    setEntwuerfe(e.data);
  };

  useEffect(() => { reload().catch((e) => setErr(formatErr(e.response?.data?.detail))); }, [id]);

  const changeTab = (t) => {
    setTab(t);
    setSearchParams(t === "ueberblick" ? {} : { tab: t });
  };

  const totalHours = useMemo(() => zeiten.reduce((s, z) => s + Number(z.stunden || 0), 0), [zeiten]);

  // Progress reference
  const progressBase = projekt ? (
    projekt.arbeitsumfang_h ||
    (projekt.designermodell === "festpreis" ? projekt.designer_kalk_stunden : null) ||
    (projekt.kundenmodell === "kontingent" ? projekt.kunden_kontingent_h : null) ||
    projekt.designer_kalk_stunden || projekt.kunden_kontingent_h || null
  ) : null;

  const pct = progressBase ? Math.min(100, (totalHours / progressBase) * 100) : 0;
  const rawPct = progressBase ? (totalHours / progressBase) * 100 : 0;
  const warnAt = projekt?.warnschwelle_prozent ?? 80;
  const overWarn = rawPct >= warnAt;
  const overFull = rawPct >= 100;

  if (err) return <div className="paper-card p-8 text-brunnerMagenta">{err}</div>;
  if (!projekt) return <div className="mono-label">{t("common.loading")}</div>;

  return (
    <div>
      <Link to="/projekte" className="mono-label inline-flex items-center gap-1.5 hover:opacity-100 mb-4" data-testid="back-projects">
        <ArrowLeft size={12} /> {t("common.back")}
      </Link>

      <div className="flex items-start justify-between mb-6 gap-6">
        <div>
          <div className="mono-label">{projekt.kunde?.firmenname} · {projekt.bereich}</div>
          <h1 className="font-archivo font-bold text-4xl mt-1">{projekt.titel}</h1>
          {projekt.archiviert && <div className="mono-label mt-2" style={{color: "#8A5A00", opacity: 1}}>{t("projekte.archived_since")} {fmtDate(projekt.archiviert_am)}</div>}
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={projekt.status} />
          {canEdit && <LifecycleMenu projekt={projekt} onReload={reload} />}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-line">
        {TABS_META.filter((tab) => !tab.hideFor || !tab.hideFor.includes(user?.rolle)).map((tabInfo) => {
          const Icon = tabInfo.icon;
          const active = tab === tabInfo.id;
          const badge = tabInfo.id === "entwuerfe" && projekt?.status === "wartet_auf_freigabe" && canEdit;
          return (
            <button
              key={tabInfo.id}
              onClick={() => changeTab(tabInfo.id)}
              data-testid={`tab-${tabInfo.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${
                active ? "border-ink text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              <Icon size={14} /> {t(tabInfo.labelKey)}
              {badge && <span className="ml-1 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#8A5A00" }} />}
            </button>
          );
        })}
      </div>

      {tab === "ueberblick" && (
        <OverviewTab projekt={projekt} entwuerfe={entwuerfe} totalHours={totalHours} progressBase={progressBase}
          pct={pct} rawPct={rawPct} overWarn={overWarn} overFull={overFull} isDesigner={isDesigner} warnAt={warnAt}
          onOpenEntwuerfe={() => changeTab("entwuerfe")} />
      )}
      {tab === "briefing" && <BriefingTab projekt={projekt} briefings={briefings} canEdit={canEdit} onReload={reload} />}
      {tab === "ci" && <KundenCITab projekt={projekt} user={user} />}
      {tab === "entwuerfe" && <EntwuerfeTab projekt={projekt} entwuerfe={entwuerfe} onReload={reload} user={user} />}
      {tab === "chat" && <ProjektChat projektId={projekt.id} />}
      {tab === "zeiten" && <ZeitenTab projekt={projekt} zeiten={zeiten} onReload={reload} user={user} />}
      {tab === "abrechnung" && canEdit && <AbrechnungTab projekt={projekt} onReload={reload} />}
    </div>
  );
}

function OverviewTab({ projekt, entwuerfe, totalHours, progressBase, pct, rawPct, overWarn, overFull, isDesigner, warnAt, onOpenEntwuerfe }) {
  const t = useT();
  const barColor = overFull ? "#BE2E6C" : overWarn ? "#8A5A00" : "#0E62C4";
  const latest = entwuerfe && entwuerfe.length > 0 ? [...entwuerfe].sort((a, b) => new Date(b.erstellt_am) - new Date(a.erstellt_am))[0] : null;
  const viewedKey = latest ? `entwurf-viewed-${latest.id}` : null;
  const [viewed, setViewed] = useState(() => viewedKey ? !!localStorage.getItem(viewedKey) : false);

  const openDraft = (e) => {
    e.stopPropagation();
    if (!latest) return;
    if (viewedKey) { localStorage.setItem(viewedKey, "1"); setViewed(true); }
    const w = window.open(); if (!w) return;
    if (latest.datei.startsWith("data:application/pdf")) w.document.write(`<iframe src="${latest.datei}" style="border:0;width:100vw;height:100vh"></iframe>`);
    else if (latest.datei.startsWith("data:image/")) w.document.write(`<img src="${latest.datei}" style="max-width:100%" alt="${latest.dateiname}"/>`);
    else { const a = document.createElement("a"); a.href = latest.datei; a.download = latest.dateiname; a.click(); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="paper-card p-6 lg:col-span-2">
        <div className="mono-label mb-4">{t("project.progress")}</div>
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="font-archivo font-bold text-3xl">{totalHours.toFixed(1)}</span>
            <span className="mono-label ml-2" style={{opacity: 1}}>{t("project.hours_logged")}</span>
          </div>
          {progressBase && (
            <div className="mono-label" style={{opacity: 1}}>
              {rawPct.toFixed(0)}% {t("project.of")} {progressBase} h
            </div>
          )}
        </div>
        {progressBase ? (
          <>
            <div className="w-full h-2 bg-line/70 rounded-sm overflow-hidden mt-3">
              <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} data-testid="progress-fill" />
            </div>
            <div className="flex justify-between mt-2 mono-label">
              <span>{t("project.warn_threshold")}: {warnAt}%</span>
              <span style={{ color: barColor, opacity: 1 }}>
                {overFull ? t("project.warn_over") : overWarn ? t("project.warn_reached") : t("project.warn_ok")}
              </span>
            </div>
          </>
        ) : (
          <div className="text-sm text-ink/60">—</div>
        )}
      </div>

      <div className="paper-card p-6">
        <div className="mono-label mb-3">{t("project.eckdaten")}</div>
        <dl className="text-sm space-y-2.5">
          <div className="flex justify-between"><dt className="text-ink/60">{t("project.startdatum")}</dt><dd className="font-mono text-xs">{fmtDate(projekt.startdatum)}</dd></div>
          <div className="flex justify-between"><dt className="text-ink/60">{t("project.deadline")}</dt><dd className="font-mono text-xs">{projekt.laufend ? t("project.deadline_laufend") : fmtDate(projekt.deadline)}</dd></div>
          <div className="flex justify-between"><dt className="text-ink/60">{t("project.bereich")}</dt><dd className="mono-label" style={{opacity:1}}>{projekt.bereich}</dd></div>
          <div className="flex justify-between"><dt className="text-ink/60">{t("project.designer")}</dt><dd>{projekt.designer?.name}</dd></div>
        </dl>
      </div>

      {latest && (
        <div className="paper-card p-6 lg:col-span-3" data-testid="overview-latest-entwurf">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-sm border border-line flex items-center justify-center shrink-0" style={{ backgroundColor: viewed ? "#F4F4F0" : "#FBEED8", borderColor: viewed ? "#DEDCD4" : "#8A5A00" }}>
                <Paperclip size={18} style={{ color: viewed ? "#1B1E23" : "#8A5A00" }} />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="mono-label" style={{ color: viewed ? "#1B1E23" : "#8A5A00", opacity: 1 }}>
                    {viewed ? t("project.latest_draft_seen") : t("project.latest_draft_new")}
                  </span>
                  <span className="mono-label">v{latest.version}</span>
                  <span className="mono-label">{fmtDate(latest.erstellt_am)}</span>
                </div>
                <div className="text-sm mt-1 font-medium truncate">{latest.dateiname}</div>
                <div className="text-xs text-ink/60 mt-0.5">{latest.hochgeladen_von} · {latest.freigabe_status}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openDraft} data-testid="overview-open-latest" className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black">
                <Download size={12} /> {t("project.open_draft")}
              </button>
              <button onClick={onOpenEntwuerfe} data-testid="overview-goto-entwuerfe" className="mono-label hover:opacity-100 px-2">
                {t("project.all_versions")}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isDesigner && (
        <div className="paper-card p-6 lg:col-span-3">
          <div className="mono-label mb-3">Konditionen</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-line rounded-sm p-4 bg-paper/40">
              <div className="mono-label mb-1" style={{ color: "#0E62C4", opacity: 1 }}>Kunde</div>
              <div className="text-sm">
                <div><strong>Modell:</strong> {projekt.kundenmodell}</div>
                {projekt.kundenmodell === "festpreis" && <div><strong>Betrag:</strong> {fmtEUR(projekt.kunden_betrag)}</div>}
                {projekt.kundenmodell === "kontingent" && <div><strong>Kontingent:</strong> {projekt.kunden_kontingent_h} h / Monat</div>}
                {projekt.kundenmodell === "pauschale" && <div><strong>Pauschale:</strong> {fmtEUR(projekt.kunden_pauschale)}</div>}
              </div>
            </div>
            <div className="border border-line rounded-sm p-4 bg-paper/40">
              <div className="mono-label mb-1" style={{ color: "#BE2E6C", opacity: 1 }}>Designer</div>
              <div className="text-sm">
                <div><strong>Modell:</strong> {projekt.designermodell}</div>
                {projekt.designermodell === "stundenbasis" && <div><strong>Satz:</strong> {fmtEUR(projekt.designer_satz)} / h</div>}
                {projekt.designermodell === "festpreis" && (
                  <>
                    <div><strong>Betrag:</strong> {fmtEUR(projekt.designer_betrag)}</div>
                    <div><strong>Kalkulierte Stunden:</strong> {projekt.designer_kalk_stunden} h</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isDesigner && (
        <div className="paper-card p-6 lg:col-span-3">
          <div className="mono-label mb-3" style={{ color: "#BE2E6C", opacity: 1 }}>Deine Kondition</div>
          <div className="text-sm">
            <div><strong>Modell:</strong> {projekt.designermodell}</div>
            {projekt.designermodell === "stundenbasis" && <div><strong>Satz:</strong> {fmtEUR(projekt.designer_satz)} / h</div>}
            {projekt.designermodell === "festpreis" && (
              <>
                <div><strong>Betrag:</strong> {fmtEUR(projekt.designer_betrag)}</div>
                <div><strong>Kalkulierte Stunden:</strong> {projekt.designer_kalk_stunden} h</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BriefingTab({ projekt, briefings, canEdit, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [ciFiles, setCiFiles] = useState([]);
  const [form, setForm] = useState({
    ausgangslage: "", ziel: "", zielgruppe: "", ci_vorgaben: "", formate: "",
    bild: "", aenderungsnotiz: "",
  });
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (projekt?.kunde?.id) {
      api.get(`/kunden/${projekt.kunde.id}/dateien`).then((r) => setCiFiles(r.data)).catch(() => setCiFiles([]));
    }
  }, [projekt?.kunde?.id]);

  const current = briefings[0]; // sorted desc
  const shown = selectedVersion ? briefings.find((b) => b.version === selectedVersion) : current;

  const openNew = () => {
    if (current) setForm({ ...current, aenderungsnotiz: "" });
    setShowForm(true);
  };

  const handleImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, bild: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/projekte/${projekt.id}/briefings`, form);
      setShowForm(false);
      await onReload();
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-1">
        <div className="paper-card p-5">
          <div className="mono-label mb-3">Versionen</div>
          {briefings.length === 0 && <div className="text-sm text-ink/50">Noch kein Briefing.</div>}
          <div className="space-y-2">
            {briefings.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelectedVersion(b.version)}
                data-testid={`briefing-v-${b.version}`}
                className={`w-full text-left px-3 py-2 border rounded-sm ${
                  (shown?.version === b.version) ? "border-ink bg-paper" : "border-line bg-white hover:border-ink/40"
                }`}
              >
                <div className="font-archivo font-bold text-sm">v{b.version}</div>
                <div className="mono-label mt-0.5" style={{opacity: 1}}>{fmtDate(b.datum)}</div>
                {b.aenderungsnotiz && <div className="text-xs text-ink/60 mt-1 truncate">{b.aenderungsnotiz}</div>}
              </button>
            ))}
          </div>

          {canEdit && (
            <button
              onClick={openNew}
              data-testid="new-briefing-btn"
              className="w-full mt-4 py-2 bg-ink text-white text-sm rounded-sm hover:bg-black transition-colors"
            >
              {briefings.length ? "Neue Version" : "Briefing anlegen"}
            </button>
          )}
        </div>

        {ciFiles.length > 0 && (
          <div className="paper-card p-5 mt-3">
            <div className="mono-label mb-3">Kunden-CI ({ciFiles.length})</div>
            <div className="space-y-1.5">
              {ciFiles.map((f) => (
                <button
                  key={f.id}
                  onClick={() => {
                    const w = window.open(); if (!w) return;
                    if (f.datei.startsWith("data:application/pdf")) w.document.write(`<iframe src="${f.datei}" style="border:0;width:100vw;height:100vh"></iframe>`);
                    else if (f.datei.startsWith("data:image/")) w.document.write(`<img src="${f.datei}" style="max-width:100%"/>`);
                    else { const a = document.createElement("a"); a.href = f.datei; a.download = f.dateiname; a.click(); }
                  }}
                  data-testid={`briefing-ci-${f.id}`}
                  className="w-full text-left px-3 py-2 border border-line rounded-sm hover:border-ink text-xs bg-white"
                >
                  <div className="mono-label" style={{opacity: 1}}>{f.kategorie.replace("_", "-")}</div>
                  <div className="truncate mt-0.5">{f.dateiname}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-3">
        {showForm ? (
          <form onSubmit={submit} className="paper-card p-6 space-y-4" data-testid="briefing-form">
            <h3 className="font-archivo font-bold text-xl">Neue Briefing-Version</h3>
            {["ausgangslage", "ziel", "zielgruppe", "ci_vorgaben", "formate"].map((f) => (
              <div key={f}>
                <label className="mono-label block mb-1.5">{f.replace("_", " ")}</label>
                <textarea
                  data-testid={`briefing-${f}`}
                  value={form[f]}
                  onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-white border border-line rounded-sm focus:outline-none focus:border-ink"
                />
              </div>
            ))}
            <div>
              <label className="mono-label block mb-1.5">Bild / Wireframe (optional)</label>
              <input type="file" accept="image/*" onChange={handleImage} data-testid="briefing-bild" className="text-sm" />
              {form.bild && <img src={form.bild} alt="preview" className="mt-3 max-h-40 rounded-sm border border-line" />}
            </div>
            <div>
              <label className="mono-label block mb-1.5">Änderungsnotiz {briefings.length ? "(Pflicht)" : ""}</label>
              <input
                type="text"
                data-testid="briefing-notiz"
                value={form.aenderungsnotiz}
                onChange={(e) => setForm((s) => ({ ...s, aenderungsnotiz: e.target.value }))}
                required={briefings.length > 0}
                className="w-full px-3 py-2 bg-white border border-line rounded-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
              <button type="submit" disabled={busy} data-testid="briefing-submit" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
                {busy ? "Speichern…" : "Briefing senden"}
              </button>
            </div>
          </form>
        ) : shown ? (
          <div className="paper-card p-8" data-testid="briefing-view">
            <div className="flex items-baseline justify-between mb-6 pb-4 border-b border-line">
              <div>
                <h3 className="font-archivo font-bold text-2xl">Briefing v{shown.version}</h3>
                <div className="mono-label mt-1">{fmtDate(shown.datum)} · {shown.verfasser}</div>
              </div>
              {shown.aenderungsnotiz && <div className="mono-label text-right max-w-xs" style={{opacity: 1}}>{shown.aenderungsnotiz}</div>}
            </div>

            {shown.bild && <img src={shown.bild} alt="briefing" className="w-full max-h-72 object-cover rounded-sm border border-line mb-6" />}

            {[
              ["ausgangslage", "Ausgangslage"],
              ["ziel", "Ziel"],
              ["zielgruppe", "Zielgruppe"],
              ["ci_vorgaben", "CI-Vorgaben"],
              ["formate", "Formate"],
            ].map(([k, label]) => (
              <div key={k} className="mb-5">
                <div className="mono-label mb-1.5">{label}</div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{shown[k] || <span className="text-ink/40">—</span>}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="paper-card p-10 text-center text-ink/50">Kein Briefing vorhanden.</div>
        )}
      </div>
    </div>
  );
}

function EntwuerfeTab({ projekt, entwuerfe, onReload, user }) {
  const role = user?.rolle;
  const isDesigner = role === "designer";
  const canReview = role === "admin" || role === "pm";
  const canUpload = isDesigner || canReview;

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [reviewFor, setReviewFor] = useState(null);
  const [reviewComment, setReviewComment] = useState("");

  // Kundenfreigabe-Flow states
  const [sendFor, setSendFor] = useState(null); // Entwurf, für den die "An Kunde senden"-Modal geöffnet ist
  const [forwardFor, setForwardFor] = useState(null); // "An Designer weiterleiten"
  const [forwardKommentar, setForwardKommentar] = useState("");

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const r = new FileReader();
    r.onload = () => setFile(r.result);
    r.readAsDataURL(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Bitte Datei wählen."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/projekte/${projekt.id}/entwuerfe`, {
        dateiname: fileName, datei: file, kommentar,
      });
      setFile(null); setFileName(""); setKommentar("");
      await onReload();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openFile = (dataUrl, name) => {
    // For data URLs, open in new tab
    const w = window.open();
    if (w) {
      if (dataUrl.startsWith("data:application/pdf")) {
        w.document.write(`<iframe src="${dataUrl}" style="border:0;width:100vw;height:100vh"></iframe>`);
      } else if (dataUrl.startsWith("data:image/")) {
        w.document.write(`<img src="${dataUrl}" style="max-width:100%" alt="${name}"/>`);
      } else {
        // trigger download
        const a = document.createElement("a"); a.href = dataUrl; a.download = name; a.click();
      }
    }
  };

  const submitReview = async (entscheidung) => {
    if (!reviewFor) return;
    setBusy(true);
    try {
      await api.post(`/projekte/${projekt.id}/entwuerfe/${reviewFor.id}/freigabe`, {
        entscheidung, kommentar: reviewComment,
      });
      setReviewFor(null); setReviewComment("");
      await onReload();
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {canUpload && (
        <div className="lg:col-span-1 paper-card p-6 h-fit">
          <h3 className="font-archivo font-bold text-lg mb-1">
            {isDesigner ? "Entwurf hochladen" : "Datei hochladen"}
          </h3>
          <div className="mono-label mb-4">PDF oder Bild</div>
          <form onSubmit={upload} className="space-y-3" data-testid="entwurf-form">
            <div>
              <label className="mono-label block mb-1">Datei</label>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={handleFile}
                data-testid="entwurf-file"
                className="text-sm w-full"
              />
              {fileName && <div className="mono-label mt-1" style={{opacity:1}}>{fileName}</div>}
            </div>
            <div>
              <label className="mono-label block mb-1">Kommentar (optional)</label>
              <textarea
                value={kommentar}
                onChange={(e) => setKommentar(e.target.value)}
                data-testid="entwurf-kommentar"
                rows={2}
                className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm"
                placeholder="z. B. Version 2 mit Farbanpassungen"
              />
            </div>
            {err && <div className="text-xs text-brunnerMagenta">{err}</div>}
            <button
              type="submit"
              disabled={busy || !file}
              data-testid="entwurf-submit"
              className="w-full py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60"
            >
              {busy ? "Lädt…" : "Hochladen"}
            </button>
            {isDesigner && (
              <div className="mono-label leading-relaxed" style={{opacity:1}}>
                Nach dem Upload wechselt der Status auf „Wartet auf Freigabe" und der PM wird benachrichtigt.
              </div>
            )}
          </form>
        </div>
      )}

      <div className={`${canUpload ? "lg:col-span-2" : "lg:col-span-3"} space-y-3`}>
        {entwuerfe.length === 0 && (
          <div className="paper-card p-10 text-center text-ink/50">Noch keine Entwürfe hochgeladen.</div>
        )}
        {entwuerfe.length > 0 && <FreigabeTimeline entwuerfe={entwuerfe} />}
        {entwuerfe.map((e) => (
          <div key={e.id} className="paper-card p-5" data-testid={`entwurf-${e.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-12 h-12 rounded-sm bg-paper border border-line flex items-center justify-center shrink-0">
                  <Paperclip size={18} className="text-ink/60" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-archivo font-bold text-sm">Entwurf v{e.version}</span>
                    <FreigabeStatus status={e.freigabe_status} />
                  </div>
                  <div className="mt-1 text-sm truncate">{e.dateiname}</div>
                  <div className="mono-label mt-1" style={{opacity:1}}>
                    {fmtDate(e.erstellt_am)} · {e.hochgeladen_von} ({e.hochgeladen_von_rolle})
                  </div>
                  {e.kommentar && <div className="text-xs text-ink/70 mt-2 italic">„{e.kommentar}"</div>}
                  {e.freigabe_kommentar && (
                    <div className="text-xs mt-2 px-3 py-2 rounded-sm bg-paper border border-line">
                      <div className="mono-label mb-1" style={{opacity:1}}>{e.freigabe_status} · {e.freigabe_von}</div>
                      {e.freigabe_kommentar}
                    </div>
                  )}
                  {/* Kundenfreigabe-Status + Kommentare */}
                  {e.kunden_status && (
                    <div className="mt-3 border-l-2 pl-3" style={{ borderLeftColor: e.kunden_status === "freigegeben" ? "#157A4A" : e.kunden_status === "kommentiert" ? "#8A5A00" : "#0E62C4" }} data-testid={`kunden-status-${e.id}`}>
                      <div className="mono-label" style={{ opacity: 1, color: e.kunden_status === "freigegeben" ? "#157A4A" : e.kunden_status === "kommentiert" ? "#8A5A00" : "#0E62C4" }}>
                        Kunde: {e.kunden_status === "versendet" ? "wartet auf Rückmeldung" : e.kunden_status === "kommentiert" ? "hat kommentiert" : "hat freigegeben"}
                        {e.kunden_versendet_an && e.kunden_versendet_an.length > 0 && <> · an {e.kunden_versendet_an.join(", ")}</>}
                      </div>
                      {(e.kunden_kommentare || []).length > 0 && (
                        <div className="mt-2 space-y-2">
                          {e.kunden_kommentare.map((k) => (
                            <div key={k.id} className="text-xs px-3 py-2 rounded-sm bg-paper border border-line">
                              <div className="mono-label mb-1" style={{opacity:1, color: k.quelle === "pm" ? "#0E62C4" : "#1B1E23"}}>
                                {k.autor_name} · {k.quelle === "pm" ? "PM" : "Kunde"}{k.is_freigabe ? " · Freigabe" : ""} · {fmtDate(k.erstellt_am)}
                              </div>
                              <div className="whitespace-pre-wrap">{k.text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={() => openFile(e.datei, e.dateiname)}
                  data-testid={`entwurf-open-${e.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-line rounded-sm text-xs hover:border-ink bg-white"
                >
                  <Download size={12} /> Öffnen
                </button>
                {canReview && !isDesigner && e.freigabe_status === "offen" && (
                  <button
                    onClick={() => setSendFor(e)}
                    data-testid={`entwurf-an-kunde-${e.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-white hover:opacity-90"
                    style={{ backgroundColor: "#0E62C4" }}
                    title={e.kunden_status ? "Erneut an Kunde senden" : "An Kunde senden"}
                  >
                    {e.kunden_status ? "Erneut senden" : "An Kunde senden"}
                  </button>
                )}
                {canReview && (e.kunden_kommentare || []).some((k) => k.quelle === "kunde") && (
                  <button
                    onClick={() => setForwardFor(e)}
                    data-testid={`entwurf-an-designer-${e.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border border-line hover:border-ink bg-white"
                  >
                    An Designer weiterleiten
                  </button>
                )}
                {canReview && e.freigabe_status === "offen" && (
                  <button
                    onClick={() => setReviewFor(e)}
                    data-testid={`entwurf-review-${e.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs bg-ink text-white hover:bg-black"
                  >
                    Prüfen
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sendFor && (
        <KundenSendenModal
          projekt={projekt}
          entwurf={sendFor}
          onClose={() => setSendFor(null)}
          onSent={async () => { setSendFor(null); await onReload(); }}
        />
      )}

      {forwardFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => setForwardFor(null)}>
          <div className="bg-white border border-line rounded-md p-6 w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="forward-modal">
            <h3 className="font-archivo font-bold text-lg mb-1">An Designer weiterleiten</h3>
            <div className="mono-label mb-4">Der Designer erhält die Kunden-Kommentare per E-Mail und in der Portal-Benachrichtigung.</div>
            <label className="mono-label block mb-1">Deine Ergänzung (optional)</label>
            <textarea value={forwardKommentar} onChange={(e) => setForwardKommentar(e.target.value)} rows={4}
              data-testid="forward-comment"
              className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm mb-4"
              placeholder="z. B. Bitte Punkt 2 nach Rücksprache berücksichtigen" />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setForwardFor(null)} className="px-3 py-2 text-sm hover:bg-line/50 rounded-sm">Abbrechen</button>
              <button
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.post(`/projekte/${projekt.id}/entwuerfe/${forwardFor.id}/an-designer`, { pm_kommentar: forwardKommentar });
                    setForwardFor(null); setForwardKommentar("");
                    await onReload();
                  } finally { setBusy(false); }
                }}
                disabled={busy}
                data-testid="forward-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-50"
              >
                Weiterleiten
              </button>
            </div>
          </div>
        </div>
      )}

      {reviewFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => setReviewFor(null)}>
          <div className="bg-white border border-line rounded-md p-6 w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="review-modal">
            <h3 className="font-archivo font-bold text-lg mb-1">Entwurf v{reviewFor.version} prüfen</h3>
            <div className="mono-label mb-4">{reviewFor.dateiname}</div>
            <textarea
              value={reviewComment}
              onChange={(ev) => setReviewComment(ev.target.value)}
              rows={4}
              placeholder="Kommentar (optional bei Freigabe, empfohlen bei Korrektur)"
              data-testid="review-comment"
              className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => submitReview("korrektur")}
                disabled={busy}
                data-testid="review-korrektur"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-line rounded-sm text-sm hover:border-brunnerAmber"
                style={{ color: "#8A5A00" }}
              >
                <RotateCcw size={14} /> Korrektur anfragen
              </button>
              <button
                onClick={() => submitReview("freigegeben")}
                disabled={busy}
                data-testid="review-freigeben"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-sm text-white hover:bg-black"
                style={{ backgroundColor: "#157A4A" }}
              >
                <Check size={14} /> Freigeben
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KundenCITab({ projekt, user }) {
  const [kunde, setKunde] = useState(null);
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!projekt?.kunde?.id) return;
    (async () => {
      try {
        const [k, f] = await Promise.all([
          api.get(`/kunden/${projekt.kunde.id}`),
          api.get(`/kunden/${projekt.kunde.id}/dateien`),
        ]);
        setKunde(k.data);
        setFiles(f.data);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail));
      }
    })();
  }, [projekt?.kunde?.id]);

  const download = (dataUrl, name) => {
    const a = document.createElement("a");
    a.href = dataUrl; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const preview = (dataUrl, name) => {
    const w = window.open(); if (!w) return;
    if (dataUrl.startsWith("data:application/pdf")) w.document.write(`<iframe src="${dataUrl}" style="border:0;width:100vw;height:100vh"></iframe>`);
    else if (dataUrl.startsWith("data:image/")) w.document.write(`<img src="${dataUrl}" style="max-width:100%" alt="${name}"/>`);
    else download(dataUrl, name);
  };

  const CATS = [
    { id: "ci_manual", label: "CI-Manual", color: "#0E62C4" },
    { id: "logo", label: "Logos", color: "#BE2E6C" },
    { id: "schrift", label: "Schriften", color: "#157A4A" },
    { id: "designbeispiel", label: "Designbeispiele", color: "#8A5A00" },
    { id: "sonstiges", label: "Sonstiges", color: "#1B1E23" },
  ];

  if (err) return <div className="paper-card p-8 text-brunnerMagenta">{err}</div>;
  if (!kunde) return <div className="mono-label">Lade CI…</div>;

  const byKat = CATS.reduce((acc, k) => { acc[k.id] = files.filter((f) => f.kategorie === k.id); return acc; }, {});
  const canEdit = user?.rolle === "admin" || user?.rolle === "pm";

  const bulkDownload = () => {
    files.forEach((f, i) => setTimeout(() => download(f.datei, f.dateiname), i * 150));
  };

  return (
    <div className="space-y-4">
      <div className="paper-card p-6">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            {kunde.logo && (
              <div className="w-16 h-16 border border-line rounded-sm bg-white flex items-center justify-center overflow-hidden shrink-0">
                <img src={kunde.logo} alt={kunde.firmenname} className="max-w-full max-h-full object-contain" />
              </div>
            )}
            <div>
              <div className="mono-label" style={{opacity: 1}}>Kunden-CI</div>
              <h3 className="font-archivo font-bold text-2xl mt-1">{kunde.firmenname}</h3>
              {kunde.domain && (
                <a href={kunde.domain.startsWith("http") ? kunde.domain : `https://${kunde.domain}`} target="_blank" rel="noreferrer" className="text-sm text-brunnerBlue hover:underline">
                  {kunde.domain}
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {files.length > 0 && (
              <button onClick={bulkDownload} data-testid="ci-download-all" className="inline-flex items-center gap-2 px-3 py-2 border border-line rounded-sm text-sm hover:border-ink bg-white">
                <Download size={14} /> Alle herunterladen ({files.length})
              </button>
            )}
            {canEdit && (
              <Link to={`/kunden/${kunde.id}`} className="mono-label hover:opacity-100">Verwalten →</Link>
            )}
          </div>
        </div>
        {kunde.ci_beschreibung && (
          <div className="mt-4 pt-4 border-t border-line">
            <div className="mono-label mb-1.5">CI-Kurzbeschreibung</div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{kunde.ci_beschreibung}</div>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <div className="paper-card p-10 text-center text-ink/50">
          Für diesen Kunden sind noch keine CI-Materialien hinterlegt.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CATS.filter((c) => byKat[c.id].length > 0).map((c) => (
            <div key={c.id} className="paper-card p-5" data-testid={`ci-cat-${c.id}`}>
              <div className="flex items-baseline justify-between mb-3 pb-3 border-b border-line">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                  <h4 className="font-archivo font-bold text-base">{c.label}</h4>
                </div>
                <span className="mono-label" style={{opacity: 1}}>{byKat[c.id].length}</span>
              </div>
              <div className="space-y-2">
                {byKat[c.id].map((f) => (
                  <div key={f.id} className="flex items-center justify-between gap-3 group" data-testid={`ci-item-${f.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate font-medium">{f.dateiname}</div>
                      <div className="mono-label mt-0.5">{fmtDate(f.erstellt_am)}{f.beschreibung ? ` · ${f.beschreibung}` : ""}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => preview(f.datei, f.dateiname)} data-testid={`ci-preview-${f.id}`} className="px-2.5 py-1 border border-line rounded-sm text-xs hover:border-ink">
                        Vorschau
                      </button>
                      <button onClick={() => download(f.datei, f.dateiname)} data-testid={`ci-dl-${f.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-ink text-white rounded-sm text-xs hover:bg-black">
                        <Download size={11} /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LifecycleMenu({ projekt, onReload }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const status = projekt.status;
  const archiviert = projekt.archiviert;

  const call = async (endpoint, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setOpen(false);
    try {
      await api.post(`/projekte/${projekt.id}/${endpoint}`);
      await onReload();
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!window.confirm("Projekt endgültig löschen?")) return;
    setOpen(false);
    try {
      await api.delete(`/projekte/${projekt.id}`);
      nav("/projekte");
    } catch (e) {
      alert(formatErr(e.response?.data?.detail));
    }
  };

  const canDelete = status === "angelegt";
  const canPause = ["briefing_versendet", "in_arbeit", "wartet_auf_freigabe", "in_abstimmung"].includes(status);
  const canResume = status === "pausiert";
  const canCancel = !["abgerechnet", "archiviert", "storniert"].includes(status) && !archiviert;
  const canComplete = ["in_arbeit", "wartet_auf_freigabe", "in_abstimmung", "pausiert", "briefing_versendet"].includes(status);
  const canArchive = !archiviert && ["abgeschlossen", "abgerechnet", "storniert"].includes(status);
  const canRestore = archiviert;

  const Item = ({ onClick, icon: Icon, label, danger, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-paper ${danger ? "text-brunnerMagenta" : ""}`}
    >
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="lifecycle-menu-btn"
        className="p-2 rounded-sm border border-line bg-white hover:border-ink"
        aria-label="Projekt-Aktionen"
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-line rounded-sm z-50 py-1" data-testid="lifecycle-menu">
            {canComplete && <Item onClick={() => call("abschliessen", "Projekt abschließen?")} icon={CheckCircle2} label="Abschließen" testId="lc-abschliessen" />}
            {canPause && <Item onClick={() => call("pausieren")} icon={Pause} label="Auf Eis legen" testId="lc-pausieren" />}
            {canResume && <Item onClick={() => call("fortsetzen")} icon={Play} label="Fortsetzen" testId="lc-fortsetzen" />}
            {canCancel && <Item onClick={() => call("stornieren", "Projekt stornieren?")} icon={Ban} label="Abbrechen / Stornieren" testId="lc-stornieren" />}
            {canArchive && <Item onClick={() => call("archivieren")} icon={Archive} label="Archivieren" testId="lc-archivieren" />}
            {canRestore && <Item onClick={() => call("wiederherstellen")} icon={ArchiveRestore} label="Wiederherstellen" testId="lc-restore" />}
            {canDelete && <>
              <div className="border-t border-line my-1" />
              <Item onClick={del} icon={Trash2} label="Löschen" danger testId="lc-loeschen" />
            </>}
            {!canDelete && !canPause && !canResume && !canCancel && !canComplete && !canArchive && !canRestore && (
              <div className="px-3 py-2 text-xs text-ink/50">Keine Aktionen verfügbar</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AbrechnungTab({ projekt, onReload }) {
  const [vorschau, setVorschau] = useState(null);
  const [abrechnungen, setAbrechnungen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    const [v, a] = await Promise.all([
      api.get(`/projekte/${projekt.id}/abrechnung/vorschau`).catch(() => ({ data: null })),
      api.get(`/projekte/${projekt.id}/abrechnungen`).catch(() => ({ data: [] })),
    ]);
    setVorschau(v.data);
    setAbrechnungen(a.data);
  };
  useEffect(() => { load(); }, [projekt?.id]);

  const abrechnen = async () => {
    setBusy(true);
    try {
      await api.post(`/projekte/${projekt.id}/abrechnung`);
      setConfirmOpen(false);
      await onReload();
      await load();
    } finally { setBusy(false); }
  };

  const isDone = projekt?.status === "abgerechnet";
  const canRun = !isDone && (projekt?.status === "abgeschlossen" || projekt?.status === "in_arbeit" || projekt?.status === "in_abstimmung" || projekt?.status === "wartet_auf_freigabe");

  const label = {
    festpreis: "Festpreis",
    stundenbasis: "Stundenbasis",
    kontingent: "Stundenkontingent",
    pauschale: "Monatspauschale",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="paper-card p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-archivo font-bold text-xl">Abrechnungs-Vorschau</h3>
          {isDone && <span className="status-pill" style={{color:"#1B1E23", backgroundColor:"#E5E4DE"}}>abgerechnet</span>}
        </div>
        {!vorschau ? (
          <div className="text-sm text-ink/50">Lade Vorschau…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="mono-label">Erfasste Stunden</div>
                <div className="font-archivo font-bold text-3xl mt-1">{vorschau.summe_stunden.toFixed(2)}</div>
              </div>
              <div>
                <div className="mono-label">Zeiteinträge</div>
                <div className="font-archivo font-bold text-3xl mt-1">{vorschau.anzahl_zeiten}</div>
              </div>
              <div>
                <div className="mono-label">Marge</div>
                <div className="font-archivo font-bold text-3xl mt-1" style={{color: vorschau.marge_eur >= 0 ? "#157A4A" : "#BE2E6C"}}>{fmtEUR(vorschau.marge_eur)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-line">
              <div className="border border-line rounded-sm p-5 bg-paper/30" data-testid="abr-kunde">
                <div className="mono-label mb-1" style={{color:"#0E62C4", opacity:1}}>Kundenseite</div>
                <div className="text-xs text-ink/60">{label[vorschau.kundenmodell] || vorschau.kundenmodell}</div>
                <div className="font-archivo font-bold text-3xl mt-3">{fmtEUR(vorschau.kunde_summe_eur)}</div>
                {vorschau.kundenmodell === "stundenbasis" && projekt.kunden_stundensatz && (
                  <div className="mono-label mt-2" style={{opacity:1}}>{vorschau.summe_stunden} h × {fmtEUR(projekt.kunden_stundensatz)}/h{projekt.kunden_stundensatz_bezeichnung ? ` (${projekt.kunden_stundensatz_bezeichnung})` : ""}</div>
                )}
              </div>
              <div className="border border-line rounded-sm p-5 bg-paper/30" data-testid="abr-designer">
                <div className="mono-label mb-1" style={{color:"#BE2E6C", opacity:1}}>Designerseite</div>
                <div className="text-xs text-ink/60">{label[vorschau.designermodell] || vorschau.designermodell}</div>
                <div className="font-archivo font-bold text-3xl mt-3">{fmtEUR(vorschau.designer_summe_eur)}</div>
                {vorschau.designermodell === "stundenbasis" && projekt.designer_satz && (
                  <div className="mono-label mt-2" style={{opacity:1}}>{vorschau.summe_stunden} h × {fmtEUR(projekt.designer_satz)}/h</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="paper-card p-6 h-fit">
        <h3 className="font-archivo font-bold text-lg mb-3">Abrechnungslauf</h3>
        {isDone ? (
          <div className="mono-label mb-3" style={{color: "#157A4A", opacity: 1}}>
            Projekt ist abgerechnet. Alle Zeiteinträge sind gesperrt.
          </div>
        ) : (
          <>
            <div className="mono-label mb-3" style={{opacity: 1}}>
              Erstellt getrennte Abrechnungen für Kunde und Designer. Alle Zeiteinträge werden anschließend gesperrt.
            </div>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={busy || !canRun}
              data-testid="abr-run-btn"
              className="w-full py-2.5 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Euro size={14} /> Abrechnen
            </button>
          </>
        )}

        {abrechnungen.length > 0 && (
          <div className="mt-6 pt-4 border-t border-line">
            <div className="mono-label mb-2">Historie</div>
            <div className="space-y-2">
              {abrechnungen.map((a) => (
                <div key={a.id} className="text-xs px-3 py-2 border border-line rounded-sm">
                  <div className="flex items-center justify-between">
                    <span className="mono-label" style={{opacity: 1, color: a.typ === "kunde" ? "#0E62C4" : "#BE2E6C"}}>{a.typ}</span>
                    <span className="font-mono">{fmtDate(a.erstellt_am)}</span>
                  </div>
                  <div className="mt-1 flex justify-between">
                    <span>{a.summe_stunden} h</span>
                    <span className="font-medium">{fmtEUR(a.summe_eur)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {confirmOpen && vorschau && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-md p-6 w-full max-w-md" data-testid="abr-confirm">
            <h3 className="font-archivo font-bold text-lg mb-2">Projekt abrechnen?</h3>
            <p className="text-sm text-ink/70 mb-4">Nach dem Abrechnungslauf sind alle {vorschau.anzahl_zeiten} Zeiteinträge dauerhaft gesperrt.</p>
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <div className="border border-line rounded-sm p-3"><div className="mono-label" style={{color:"#0E62C4", opacity:1}}>Kunde</div><div className="font-archivo font-bold text-xl mt-1">{fmtEUR(vorschau.kunde_summe_eur)}</div></div>
              <div className="border border-line rounded-sm p-3"><div className="mono-label" style={{color:"#BE2E6C", opacity:1}}>Designer</div><div className="font-archivo font-bold text-xl mt-1">{fmtEUR(vorschau.designer_summe_eur)}</div></div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 border border-line rounded-sm">Abbrechen</button>
              <button onClick={abrechnen} disabled={busy} data-testid="abr-confirm-yes" className="px-4 py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
                {busy ? "Läuft…" : "Jetzt abrechnen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function FreigabeStatus({ status }) {
  const map = {
    offen:        { label: "offen",        color: "#8A5A00", bg: "#FBEED8" },
    freigegeben:  { label: "freigegeben",  color: "#157A4A", bg: "#DDEEE3" },
    korrektur:    { label: "Korrektur",    color: "#BE2E6C", bg: "#F7DDE7" },
  };
  const s = map[status] || map.offen;
  return (
    <span className="status-pill" style={{ color: s.color, backgroundColor: s.bg }}>{s.label}</span>
  );
}

function FreigabeTimeline({ entwuerfe }) {
  // Build chronological events oldest->newest
  const events = [];
  const sorted = [...entwuerfe].sort((a, b) => new Date(a.erstellt_am) - new Date(b.erstellt_am));
  sorted.forEach((e) => {
    events.push({
      type: "upload",
      version: e.version,
      datum: e.erstellt_am,
      author: e.hochgeladen_von,
      rolle: e.hochgeladen_von_rolle,
      kommentar: e.kommentar,
    });
    if (e.freigabe_status && e.freigabe_status !== "offen") {
      const delta = e.freigabe_am ? (new Date(e.freigabe_am) - new Date(e.erstellt_am)) / 3600000 : null;
      events.push({
        type: e.freigabe_status,
        version: e.version,
        datum: e.freigabe_am,
        author: e.freigabe_von,
        kommentar: e.freigabe_kommentar,
        delta_h: delta,
      });
    }
  });

  const dotStyle = (type) => {
    if (type === "upload") return { color: "#BE2E6C", label: "Upload" };
    if (type === "freigegeben") return { color: "#157A4A", label: "Freigabe" };
    if (type === "korrektur") return { color: "#8A5A00", label: "Korrektur" };
    return { color: "#1B1E23", label: type };
  };

  return (
    <div className="paper-card p-6" data-testid="freigabe-timeline">
      <div className="flex items-baseline justify-between mb-5">
        <h3 className="font-archivo font-bold text-lg">Freigabe-Historie</h3>
        <span className="mono-label">{sorted.length} Version{sorted.length === 1 ? "" : "en"}</span>
      </div>

      <div className="relative pl-6">
        <div className="absolute left-[7px] top-1 bottom-1 w-px bg-line" />
        <ol className="space-y-4">
          {events.map((ev, idx) => {
            const s = dotStyle(ev.type);
            return (
              <li key={idx} className="relative" data-testid={`tl-event-${idx}`}>
                <span
                  className="absolute -left-6 top-1.5 w-3 h-3 rounded-full ring-2 ring-white"
                  style={{ backgroundColor: s.color }}
                />
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="mono-label" style={{ color: s.color, opacity: 1 }}>
                    {s.label} · v{ev.version}
                  </span>
                  <span className="mono-label">{fmtDate(ev.datum)}</span>
                  {ev.delta_h != null && (
                    <span className="mono-label" style={{opacity: 1}}>
                      · Δ {ev.delta_h < 1 ? `${Math.round(ev.delta_h * 60)} min` : ev.delta_h < 48 ? `${ev.delta_h.toFixed(1)} h` : `${(ev.delta_h/24).toFixed(1)} d`}
                    </span>
                  )}
                </div>
                <div className="text-sm mt-0.5">
                  <span className="text-ink/70">{ev.author}</span>
                  {ev.rolle && <span className="mono-label ml-2">{ev.rolle}</span>}
                </div>
                {ev.kommentar && (
                  <div className="text-xs text-ink/70 italic mt-1 border-l-2 pl-2" style={{ borderColor: s.color }}>
                    „{ev.kommentar}"
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}



function ZeitenTab({ projekt, zeiten, onReload, user }) {
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [stunden, setStunden] = useState("");
  const [taetigkeit, setTaetigkeit] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const canWrite = user?.rolle === "admin" || user?.rolle === "pm" || user?.rolle === "designer";

  const submit = async (e) => {
    e.preventDefault();
    if (!stunden || !taetigkeit) return;
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/projekte/${projekt.id}/zeiten`, {
        projekt_id: projekt.id, datum, stunden: parseFloat(stunden), taetigkeit,
      });
      if (data.warn_triggered) setMsg(`Warnschwelle ${data.warn_triggered}% erreicht – PM wurde benachrichtigt.`);
      setStunden(""); setTaetigkeit("");
      await onReload();
    } catch (e) {
      setMsg("Fehler beim Speichern.");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await api.delete(`/zeiten/${id}`);
    await onReload();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {canWrite && (
        <div className="lg:col-span-1 paper-card p-6 h-fit">
          <h3 className="font-archivo font-bold text-lg mb-4">Zeit erfassen</h3>
          <form onSubmit={submit} className="space-y-3" data-testid="zeit-form">
            <div>
              <label className="mono-label block mb-1">Datum</label>
              <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="zeit-datum"
                     className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm" />
            </div>
            <div>
              <label className="mono-label block mb-1">Stunden</label>
              <input type="number" step="0.25" min="0.25" value={stunden} onChange={(e) => setStunden(e.target.value)} data-testid="zeit-stunden"
                     className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm" required />
            </div>
            <div>
              <label className="mono-label block mb-1">Tätigkeit</label>
              <input type="text" value={taetigkeit} onChange={(e) => setTaetigkeit(e.target.value)} data-testid="zeit-taetigkeit"
                     className="w-full px-3 py-2 bg-white border border-line rounded-sm text-sm" required />
            </div>
            <button type="submit" disabled={busy} data-testid="zeit-submit"
                    className="w-full py-2 bg-ink text-white rounded-sm hover:bg-black disabled:opacity-60">
              {busy ? "Speichern…" : "Eintrag speichern"}
            </button>
            {msg && (
              <div className="text-xs px-3 py-2 rounded-sm bg-brunnerAmber/10 text-brunnerAmber border border-brunnerAmber/30" data-testid="zeit-warn">
                {msg}
              </div>
            )}
          </form>
        </div>
      )}

      <div className="lg:col-span-2 paper-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-paper/60">
              <th className="text-left mono-label py-3 px-4 font-medium">Datum</th>
              <th className="text-left mono-label py-3 px-4 font-medium">Verfasser</th>
              <th className="text-left mono-label py-3 px-4 font-medium">Tätigkeit</th>
              <th className="text-right mono-label py-3 px-4 font-medium">Stunden</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {zeiten.map((z) => (
              <tr key={z.id} className="border-b border-line/60 last:border-0" data-testid={`zeit-row-${z.id}`}>
                <td className="py-3 px-4 font-mono text-xs">{fmtDate(z.datum)}</td>
                <td className="py-3 px-4">{z.verfasser}</td>
                <td className="py-3 px-4">{z.taetigkeit}</td>
                <td className="py-3 px-4 text-right font-mono">{Number(z.stunden).toFixed(2)}</td>
                <td className="py-3 px-2 text-right">
                  {!z.abgerechnet && (user?.id === z.verfasser_id || user?.rolle === "admin" || user?.rolle === "pm") && (
                    <button onClick={() => del(z.id)} className="text-xs text-ink/40 hover:text-brunnerMagenta" data-testid={`zeit-del-${z.id}`}>×</button>
                  )}
                </td>
              </tr>
            ))}
            {zeiten.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-ink/50">Keine Einträge</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function KundenSendenModal({ projekt, entwurf, onClose, onSent }) {
  const [kunde, setKunde] = useState(null);
  const [empfaenger, setEmpfaenger] = useState({ name: "", email: "" });
  const [cc, setCc] = useState([]);
  const [pmNotiz, setPmNotiz] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/kunden/${projekt.kunde.id}`);
        setKunde(data);
        const aps = data.ansprechpartner || [];
        // pre-select hauptkontakt if set, else first
        let picked = null;
        if (projekt.hauptkontakt_id != null) {
          picked = aps[projekt.hauptkontakt_id] || null;
        }
        if (!picked && aps.length > 0) picked = aps[0];
        if (picked) setEmpfaenger({ name: picked.name || "", email: picked.email || "" });
      } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
    })();
  }, [projekt.kunde.id, projekt.hauptkontakt_id]);

  const submit = async () => {
    if (!empfaenger.email) { setErr("E-Mail-Adresse des Empfängers fehlt."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/projekte/${projekt.id}/entwuerfe/${entwurf.id}/an-kunde`, {
        empfaenger_email: empfaenger.email,
        empfaenger_name: empfaenger.name,
        cc_emails: cc,
        pm_notiz: pmNotiz,
      });
      await onSent();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const aps = kunde?.ansprechpartner || [];
  const toggleCc = (email) => {
    setCc((s) => s.includes(email) ? s.filter((x) => x !== email) : [...s, email]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div className="bg-white border border-line rounded-md p-6 w-full max-w-lg" onClick={(ev) => ev.stopPropagation()} data-testid="kunde-senden-modal">
        <h3 className="font-archivo font-bold text-lg mb-1">Entwurf an Kunde senden</h3>
        <div className="mono-label mb-4">Der Kunde erhält einen Link zum Entwurf „{entwurf.dateiname}" (v{entwurf.version}) und kann direkt kommentieren oder freigeben.</div>

        <div className="mb-3">
          <label className="mono-label block mb-1">Hauptempfänger *</label>
          {aps.length > 0 ? (
            <select
              value={empfaenger.email}
              onChange={(ev) => {
                const a = aps.find((x) => x.email === ev.target.value);
                setEmpfaenger({ email: ev.target.value, name: a?.name || "" });
              }}
              data-testid="kunde-senden-empfaenger"
              className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm"
            >
              <option value="">— wählen —</option>
              {aps.map((a, i) => (
                <option key={i} value={a.email || ""}>{a.name} {a.email ? `<${a.email}>` : ""}</option>
              ))}
            </select>
          ) : (
            <div className="text-sm text-ink/60">Keine Ansprechpartner beim Kunden hinterlegt. Bitte zuerst im Kunden-Datensatz einen Kontakt anlegen.</div>
          )}
        </div>

        {aps.filter((a) => a.email && a.email !== empfaenger.email).length > 0 && (
          <div className="mb-3">
            <label className="mono-label block mb-1">Kopie an (optional)</label>
            <div className="flex flex-wrap gap-1.5">
              {aps.filter((a) => a.email && a.email !== empfaenger.email).map((a, i) => {
                const on = cc.includes(a.email);
                return (
                  <button key={i} type="button" onClick={() => toggleCc(a.email)}
                    data-testid={`kunde-senden-cc-${i}`}
                    className={`text-xs px-2.5 py-1 rounded-sm border ${on ? "bg-ink text-white border-ink" : "bg-white border-line hover:border-ink"}`}>
                    {a.name || a.email}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="mono-label block mb-1">Nachricht an den Kunden (optional)</label>
          <textarea value={pmNotiz} onChange={(ev) => setPmNotiz(ev.target.value)} rows={4}
            data-testid="kunde-senden-notiz"
            className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm"
            placeholder="Wird oberhalb des Entwurfs angezeigt und in der Mail zitiert." />
        </div>

        {err && <div className="text-sm mb-3" style={{ color: "#BE2E6C" }}>{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm hover:bg-line/50 rounded-sm">Abbrechen</button>
          <button
            onClick={submit}
            disabled={busy || !empfaenger.email}
            data-testid="kunde-senden-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-50"
          >
            {busy ? "Sendet…" : "Jetzt senden"}
          </button>
        </div>
      </div>
    </div>
  );
}
